import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import StaffDashboard from "./pages/StaffDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import LeadsPage from "./pages/LeadsPage";
import ClientsPage from "./pages/ClientsPage";
import TasksPage from "./pages/TasksPage";
import VisitsPage from "./pages/VisitsPage";
import DocumentsPage from "./pages/DocumentsPage";
import InvoicesPage from "./pages/InvoicesPage";
import TicketsPage from "./pages/TicketsPage";
import UsersPage from "./pages/UsersPage";
import AIToolsPage from "./pages/AIToolsPage";
import PhaseManagementPage from "./pages/PhaseManagementPage";
import PhaseMasterPage from "./pages/PhaseMasterPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import ReportsPage from "./pages/ReportsPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import RosterPage from "./pages/RosterPage";
import LeavesPage from "./pages/LeavesPage";
import AccreditationStandardsPage from "./pages/AccreditationStandardsPage";
import RBACSettingsPage from "./pages/RBACSettingsPage";
import AttendancePage from "./pages/AttendancePage";

import "./App.css";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Admin/Super Admin Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <AdminDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Staff Routes */}
          <Route
            path="/staff-dashboard"
            element={
              <ProtectedRoute allowedRoles={["staff"]}>
                <AppLayout>
                  <StaffDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Client Routes */}
          <Route
            path="/client-dashboard"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <AppLayout>
                  <ClientDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Leads - Admin/Staff */}
          <Route
            path="/leads"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <LeadsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Clients */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <ClientsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Phases - dedicated management page */}
          <Route
            path="/phases"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <PhaseManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Phase Master - Admin overview of all clients */}
          <Route
            path="/phase-master"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <PhaseMasterPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Accreditation Standards */}
          <Route
            path="/accreditation-standards"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <AccreditationStandardsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Access Control (RBAC) — Super Admin only */}
          <Route
            path="/access-control"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <AppLayout>
                  <RBACSettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* My Progress - Client */}
          <Route
            path="/my-progress"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <AppLayout>
                  <PhaseManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Tasks */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff", "client"]}>
                <AppLayout>
                  <TasksPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Roster - Admin/Staff */}
          <Route
            path="/roster"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <RosterPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Leaves - Admin/Staff */}
          <Route
            path="/leaves"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <LeavesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Attendance - Admin/Staff */}
          <Route
            path="/attendance"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <AttendancePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Visits */}
          <Route
            path="/visits"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff", "client"]}>
                <AppLayout>
                  <VisitsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Documents */}
          <Route
            path="/documents"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff", "client"]}>
                <AppLayout>
                  <DocumentsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Invoices */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "client"]}>
                <AppLayout>
                  <InvoicesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Tickets */}
          <Route
            path="/tickets"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff", "client"]}>
                <AppLayout>
                  <TicketsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Users - Admin only */}
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <UsersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* AI Tools - Admin/Staff */}
          <Route
            path="/ai-tools"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager", "staff"]}>
                <AppLayout>
                  <AIToolsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Communications Master - Admin only */}
          <Route
            path="/communications"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <CommunicationsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Audit Trail - Admin only */}
          <Route
            path="/audit-trail"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <AuditTrailPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Consolidated Reports - Admin / Manager only */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <ReportsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Analytics Dashboard - Admin only */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "admin", "manager"]}>
                <AppLayout>
                  <AnalyticsDashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Client Self-Service Portal */}
          <Route
            path="/my-portal"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <AppLayout>
                  <ClientPortalPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
